import { TransportSedel } from "types/transportSedel.type";
import { Uppdrag, UppdragStatus } from "types/uppdrag.type";
import { getRelevantWasteWeight, Waste } from "types/waste.type";
import { WasteModal } from "./WasteModal";
import { useDeleteWasteLogic } from "./useDeleteWasteLogic";
import DangerModal from "components/DangerModal";
import EmptyState from "components/EmptyState";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";
import { DotMenu } from "components/DotMenu";
import { useCreateWasteLogic } from "./useCreateWasteLogic";
import { useUpdateWasteLogic } from "./useUpdateWasteLogic";
import { Tag } from "components/Tag";
import BasicTable from "components/basicTable/BasicTable";
import { Headline4 } from "components/typography/Headline4";
import { Link } from "components/Link";
import { SeparatorIcon } from "components/icons/SeparatorIcon";
import SecondaryButton from "components/buttons/SecondaryButton";
import { TrashIcon } from "@heroicons/react/24/outline";
import { User } from "types/user.type";
import { useCurrentUser } from "api/user.api";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { Headline5 } from "components/typography/Headline5";
type Props = {
  transportSedel: TransportSedel;
  uppdrag: Uppdrag;
};
export const TransportSedelItemWasteList = ({
  transportSedel,
  uppdrag,
}: Props) => {
  const createWasteLogic = useCreateWasteLogic({ transportSedel, uppdrag });
  return (
    <div className="mt-12">
      <WasteModal logic={createWasteLogic} title={"Nytt avfall"} />
      <div className="mb-2 flex items-center justify-between">
        <Headline4>Avfall</Headline4>
        {transportSedel.wasteList.length > 0 && (
          <SecondaryButton size="small" onClick={createWasteLogic.openModal}>
            + Nytt avfall
          </SecondaryButton>
        )}
      </div>
      {transportSedel.wasteList.length > 0 ? (
        <>
          <MobileList uppdrag={uppdrag} transportSedel={transportSedel} />
          <DesktopList uppdrag={uppdrag} transportSedel={transportSedel} />
        </>
      ) : (
        <EmptyState
          onClick={createWasteLogic.openModal}
          header="Inget avfall"
          subHeader=""
          buttonText="Nytt avfall"
          iconType="truck"
        />
      )}
    </div>
  );
};

type ItemProps = Props & {
  waste: Waste;
  currentUser?: User;
};

const DesktopList = ({ transportSedel, uppdrag }: Props) => {
  const { user: currentUser } = useCurrentUser();
  return (
    <BasicTable className="hidden sm:block">
      <BasicTable.THead>
        <BasicTable.TR>
          <BasicTable.TH>Avfallskod</BasicTable.TH>
          <BasicTable.TH>Objekt</BasicTable.TH>
          <BasicTable.TH>Vikt</BasicTable.TH>
          <BasicTable.TH></BasicTable.TH>
        </BasicTable.TR>
      </BasicTable.THead>
      <BasicTable.TBody>
        {transportSedel.wasteList
          .sort((a, b) => (a.id < b.id ? -1 : 1))
          .map((w) => (
            <DesktopItem
              key={w.id}
              waste={w}
              uppdrag={uppdrag}
              transportSedel={transportSedel}
              currentUser={currentUser}
            />
          ))}
      </BasicTable.TBody>
    </BasicTable>
  );
};

const DesktopItem = ({
  transportSedel,
  waste,
  uppdrag,
  currentUser,
}: ItemProps) => {
  const editWasteLogic = useUpdateWasteLogic(transportSedel, uppdrag, waste);
  const deleteWasteLogic = useDeleteWasteLogic();

  return (
    <BasicTable.TR>
      <BasicTable.TD>
        <b>{waste.wasteCode.codePretty}</b>
      </BasicTable.TD>
      <BasicTable.TD className="max-w-48 truncate">
        {waste.tomning?.separator && uppdrag?.facility ? (
          <Link
            to="/app/kunder/$customerId/facility/$facilityId/separator/$separatorId"
            params={{
              customerId: uppdrag.facility.customer.id.toString(),
              facilityId: uppdrag.facility.id.toString(),
              separatorId: waste.tomning.separator.id.toString(),
            }}
            variant="gray"
          >
            {getSeparatorDetailedLabel(waste.tomning.separator)}
          </Link>
        ) : (
          <></>
        )}
      </BasicTable.TD>
      <BasicTable.TD>
        <span className="flex items-center">
          {getRelevantWasteWeight(waste)}
          {!!waste.weightFinal && (
            <Tag color="cyan" text="Vägd" className="ml-2" />
          )}
          {!getRelevantWasteWeight(waste) &&
            uppdrag.status !== UppdragStatus.Scheduled &&
            !!currentUser &&
            currentUser?.id === uppdrag.assignedToUser?.id && (
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon
                  className="h-5 w-5 text-yellow-600"
                  aria-hidden="true"
                />
                <Headline5>Vikt saknas</Headline5>
              </div>
            )}
        </span>
      </BasicTable.TD>
      <BasicTable.TD className="flex justify-end text-right">
        <DotMenu
          items={[
            {
              label: "Redigera",
              onClick: editWasteLogic.openModal,
            },
            {
              label: "Ta bort",
              onClick: deleteWasteLogic.openModal,
              isDelete: true,
            },
          ]}
          className="mt-1"
        />
      </BasicTable.TD>
      <WasteModal logic={editWasteLogic} title={"Redigera avfall"} />
      <DangerModal
        title="Ta bort avfall"
        isOpen={deleteWasteLogic.isModalOpen}
        onClose={deleteWasteLogic.onModalClose}
        actionHandler={() =>
          deleteWasteLogic.onConfirm({
            transportSedelId: transportSedel.id,
            wasteId: waste.id,
          })
        }
      />
    </BasicTable.TR>
  );
};

const MobileList = ({ transportSedel, uppdrag }: Props) => {
  const { user: currentUser } = useCurrentUser();
  return (
    <div className="flex flex-col pt-4 sm:hidden">
      {transportSedel.wasteList
        .sort((a, b) => (a.id < b.id ? -1 : 1))
        .map((w) => (
          <MobileItem
            key={w.id}
            waste={w}
            uppdrag={uppdrag}
            transportSedel={transportSedel}
            currentUser={currentUser}
          />
        ))}
    </div>
  );
};
const MobileItem = ({
  transportSedel,
  waste,
  uppdrag,
  currentUser,
}: ItemProps) => {
  const editWasteLogic = useUpdateWasteLogic(transportSedel, uppdrag, waste);
  const deleteWasteLogic = useDeleteWasteLogic();
  return (
    <div className="mb-4 flex flex-col rounded-md border border-gray-100 p-4 shadow-md">
      {!!waste.tomning?.separator && (
        <Link
          className="mb-3 flex items-center truncate text-xs"
          to="/app/kunder/$customerId/facility/$facilityId/separator/$separatorId"
          params={{
            customerId: uppdrag.facility.customer.id.toString(),
            facilityId: uppdrag.facility.id.toString(),
            separatorId: waste.tomning.separator.id.toString(),
          }}
          variant="gray"
        >
          <SeparatorIcon className="mr-1" />{" "}
          {getSeparatorDetailedLabel(waste.tomning.separator)}
        </Link>
      )}
      <p className="text-base font-semibold">
        <span className="mr-1">Avfallskod:</span>
        {waste.wasteCode.codePretty}
      </p>
      <p className="mb-3 text-sm text-gray-600">
        {waste.wasteCode.description}
      </p>
      <p className="flex items-center font-semibold">
        {getRelevantWasteWeight(waste)}
        {!!waste.weightFinal && (
          <Tag color="cyan" text="Vägd" className="ml-2" />
        )}
        {!getRelevantWasteWeight(waste) &&
          uppdrag.status !== UppdragStatus.Scheduled &&
          !!currentUser &&
          currentUser?.id === uppdrag.assignedToUser?.id && (
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon
                className="h-5 w-5 text-yellow-600"
                aria-hidden="true"
              />
              <Headline5>Vikt saknas</Headline5>
            </div>
          )}
      </p>
      <div className="mt-5 flex items-center justify-between">
        <button className="rounded-full border border-gray-100 bg-gray-25 p-1">
          <TrashIcon
            className="h-5 w-5 text-red-500"
            onClick={deleteWasteLogic.openModal}
          />
        </button>
        <DangerModal
          title="Ta bort avfall"
          isOpen={deleteWasteLogic.isModalOpen}
          onClose={deleteWasteLogic.onModalClose}
          actionHandler={() =>
            deleteWasteLogic.onConfirm({
              transportSedelId: transportSedel.id,
              wasteId: waste.id,
            })
          }
        />
        <SecondaryButton onClick={editWasteLogic.openModal}>
          Redigera
        </SecondaryButton>
        <WasteModal logic={editWasteLogic} title={"Redigera avfall"} />
      </div>
    </div>
  );
};
